"use client";

import { useEffect, useSyncExternalStore } from "react";
import { usePathname } from "next/navigation";
import { getAccountSessionSnapshot, subscribeAccountSession } from "@/lib/account-session";
import { resolveCustomerJourneyIdentity } from "@/lib/customer-journey-identity";
import { getLastReadingSnapshot, subscribeLastReading } from "@/lib/last-reading";
import {
  isHindiMobileJourney,
  mobileNavKeyForPath,
  type MobileNavKey,
} from "./mobile-bottom-bar-path";

const NAV_ID = "mobile-bottom-navigation";

function syncNavigation(
  pathname: string,
  journeyHref: string,
  journeyLabel: "My Jyotishi" | "My Reading"
): void {
  const navigation = document.getElementById(NAV_ID);
  if (!(navigation instanceof HTMLElement)) return;

  const language = isHindiMobileJourney(pathname) ? "hi" : "en";
  const activeKey = mobileNavKeyForPath(pathname);
  navigation.lang = language;
  navigation.setAttribute(
    "aria-label",
    language === "hi" ? "मुख्य मोबाइल नेविगेशन" : "Primary mobile navigation"
  );

  navigation.querySelectorAll<HTMLAnchorElement>("a[data-mobile-nav-key]").forEach((link) => {
    const key = link.dataset.mobileNavKey as MobileNavKey | undefined;
    const localizedHref = language === "hi" ? link.dataset.hiHref : link.dataset.enHref;
    link.setAttribute("href", key === "journey" ? journeyHref : localizedHref || "/");
    const active = key === activeKey;
    link.toggleAttribute("aria-current", active);
    if (active) link.setAttribute("aria-current", "page");
    const label = link.querySelector("[data-mobile-nav-label]");
    if (label) {
      if (key === "journey" && journeyLabel === "My Reading") {
        label.textContent = language === "hi" ? "मेरी रीडिंग" : journeyLabel;
      } else {
        label.textContent =
          language === "hi" ? link.dataset.hiLabel || "" : link.dataset.enLabel || "";
      }
    }
  });
}

function normalizePath(path: string): string {
  return path.replace(/\/+$/, "") || "/";
}

function clearPending(): void {
  document
    .querySelectorAll(`#${NAV_ID} a[aria-busy]`)
    .forEach((link) => link.removeAttribute("aria-busy"));
}

/**
 * Tab taps (C25). The bar is plain <a> on purpose (no next/link prefetch; see
 * the #2510 prefetch loop), so:
 *  - tapping the tab for the page you are on scrolled nowhere and did a full
 *    reload; it now scrolls to the top instead, like a native tab bar;
 *  - a tap on another tab gets a pending state until the next document
 *    commits, cleared on a back/forward-cache restore so it can never stick.
 */
function onNavigationClick(event: MouseEvent): void {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const link = (event.target as Element | null)?.closest?.("a[data-mobile-nav-key]");
  if (!(link instanceof HTMLAnchorElement)) return;
  const target = new URL(link.href, window.location.href);
  const current =
    link.getAttribute("aria-current") === "page" &&
    normalizePath(target.pathname) === normalizePath(window.location.pathname);
  if (current) {
    event.preventDefault();
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
    return;
  }
  clearPending();
  link.setAttribute("aria-busy", "true");
}

function onPageShow(event: PageTransitionEvent): void {
  if (event.persisted) clearPending();
}

export default function MobileBottomBarState() {
  const pathname = usePathname() || "/";
  const accountSession = useSyncExternalStore(
    subscribeAccountSession,
    getAccountSessionSnapshot,
    () => null
  );
  const rememberedReading = useSyncExternalStore(
    subscribeLastReading,
    getLastReadingSnapshot,
    () => null
  );
  const journey = resolveCustomerJourneyIdentity({
    accountSession,
    authSubject: null,
    rememberedReading,
  });
  useEffect(
    () => syncNavigation(pathname, journey.primaryHref, journey.primaryLabel),
    [journey.primaryHref, journey.primaryLabel, pathname]
  );
  useEffect(() => {
    const navigation = document.getElementById(NAV_ID);
    navigation?.addEventListener("click", onNavigationClick);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      navigation?.removeEventListener("click", onNavigationClick);
      window.removeEventListener("pageshow", onPageShow);
      clearPending();
    };
  }, [pathname]);
  return null;
}
